class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.totalCounts = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.noOverflow = this.getAttribute('data-no-overflow');
    this.inputType = this.querySelector('input[name=type]');
    this.headerSection = document.querySelector('.pd-header-section');
    this.headerInput = this.querySelector('.pd-header input[type="search"]');
    this.buttonCancel = this.querySelector('.js-search-form-cancel');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input?.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input?.addEventListener('focus', this.onFocus.bind(this));
    if (window.innerWidth > 750) {
      this.addEventListener('focusout', this.onFocusOut.bind(this));
    }
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.headerInput?.addEventListener('input', e => {
      if (e.target.value != '') {
        this.toggleExpandedSearch(true);
      }
    });

    // Expand the search bar as soon as it's focused, not only after typing.
    // The collapse happens through focusout -> close().
    this.headerInput?.addEventListener('focus', () => {
      this.toggleExpandedSearch(true);
    });

    this.buttonCancel?.addEventListener('click', () => {
      this.close();
    })
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      document.documentElement.classList.remove('overflow-hidden');
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  fetchPredictiveSearch(searchTerm, queryKey) {
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  fetchPredictiveSearchProduct(searchTerm, queryKey) {
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search-product`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search-product').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();
    this.fetchTotalCount(searchTerm);

    // if (this.cachedResults[queryKey]) {
    //   this.renderSearchResults(this.cachedResults[queryKey]);
    //   return;
    // }

    if (this.inputType && this.inputType.value === 'product') {
      this.fetchPredictiveSearchProduct(searchTerm, queryKey);
    } else {
      this.fetchPredictiveSearch(searchTerm, queryKey);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    const totalResult = this.querySelectorAll('#predictive-search-results li[role="option"]').length;
    this.resultsDisplayedNumber = this.querySelector('#results-displayed-number');

    if (this.resultsDisplayedNumber && !this.resultsDisplayedNumber.textContent.trim()) {
      this.resultsDisplayedNumber.innerHTML = totalResult.toString();
    }

    this.applyTotalCount(this.searchTerm);
  }

  // OC1-330 review: the predictive API caps resources per type, so the Liquid
  // total can never exceed what is shown ("7/7 results displayed"). The real
  // total comes from the main-search-count section, one request per result
  // type, because Shopify's combined-type count is pruned (27 vs 236).
  // Runs alongside the predictive fetch and is cached per term.
  fetchTotalCount(searchTerm) {
    const type = this.inputType?.value;
    const types = type ? [type] : ['product', 'page', 'article'];
    const key = `${type || 'all'}:${searchTerm}`;
    if (this.totalCounts[key]) return this.totalCounts[key];

    const base = window.routes?.search_url || '/search';
    this.totalCounts[key] = Promise.all(
      types.map((t) =>
        fetch(
          `${base}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last` +
            `&type=${encodeURIComponent(t)}&section_id=main-search-count`
        )
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((text) => {
            const el = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-search-count]');
            const count = parseInt(el?.textContent, 10);
            if (Number.isNaN(count)) throw new Error('count missing');
            return count;
          })
      )
    )
      .then((counts) => counts.reduce((sum, count) => sum + count, 0))
      .catch(() => {
        delete this.totalCounts[key];
        return null;
      });
    return this.totalCounts[key];
  }

  applyTotalCount(searchTerm) {
    const wrapper = this.querySelector('[data-predictive-search-count]');
    if (!wrapper) return;

    this.fetchTotalCount(searchTerm).then((total) => {
      // A newer render has replaced this footer, or the term moved on.
      if (!wrapper.isConnected || searchTerm !== this.searchTerm) return;

      const totalEl = wrapper.querySelector('[data-predictive-search-total]');
      if (total !== null && totalEl) {
        const shown = parseInt(wrapper.querySelector('#results-displayed-number')?.textContent, 10) || 0;
        totalEl.textContent = Math.max(total, shown).toString();
      }
      wrapper.removeAttribute('data-pending');
      this.setLiveRegionText(wrapper.textContent.trim());
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    !this.noOverflow && document.documentElement.classList.add('overflow-hidden');
    this.classList.add('has-term');
    const announcementBarRect = document.querySelector('.pd-announcement-bar-section')?.getBoundingClientRect();
    const searchFormRect = document.querySelector('.pd-header-section')?.getBoundingClientRect();
    const announcementBarRectHeight = announcementBarRect ? announcementBarRect.height : 0;
    const searchFormHeight = searchFormRect ? searchFormRect.height : 0;
    let overlayTopOffset = announcementBarRectHeight + searchFormHeight;
    if (window.innerWidth < 750) {
      overlayTopOffset = announcementBarRectHeight + searchFormHeight + 40; // 40 is searchform's heigh on mobile layout
    }
    document.querySelector('.predictive-search-overlay').style.top = `${overlayTopOffset}px`;

    // The header panel is fixed and full-bleed, so it needs the same computed
    // offset as the overlay. The hero banner instance keeps its dropdown CSS.
    if (window.innerWidth >= 750 && this.closest('.pd-search-form')) {
      this.predictiveSearchResults.style.top = `${overlayTopOffset}px`;
    }
  }

  // FLIP-animate the header form between its collapsed and expanded layouts:
  // measure, flip the class, measure again, then play the delta as a width +
  // transform transition so the class swap itself never causes a visible jump.
  toggleExpandedSearch(expand) {
    const active = this.headerSection.classList.contains('header--active-search');
    if (expand === active) return;

    const form = this.headerInput?.form;
    if (!form || window.innerWidth < 750) {
      this.headerSection.classList.toggle('header--active-search', expand);
      return;
    }

    this.expandAnimation?.cancel();

    const first = form.getBoundingClientRect();
    this.headerSection.classList.toggle('header--active-search', expand);
    const last = form.getBoundingClientRect();
    if (first.left === last.left && first.top === last.top && first.width === last.width) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Animating width shifts an in-flow element's own layout position, so pin
    // the form to fixed positioning for the duration and animate the viewport
    // rect directly. The wrapper keeps the collapsed slot reserved so the rest
    // of the header never reflows mid-animation.
    const wrapper = form.closest('.pd-search-form');
    const collapsedWidth = expand ? first.width : last.width;
    const previousInlineStyle = form.getAttribute('style') || '';
    if (wrapper) {
      wrapper.style.width = `${collapsedWidth}px`;
      wrapper.style.height = `${first.height}px`;
    }
    form.style.position = 'fixed';
    form.style.left = `${last.left}px`;
    form.style.top = `${last.top}px`;
    form.style.width = `${last.width}px`;
    form.style.maxWidth = 'none';
    form.style.margin = '0';
    form.style.zIndex = '20';

    const animation = form.animate(
      [
        { left: `${first.left}px`, top: `${first.top}px`, width: `${first.width}px` },
        { left: `${last.left}px`, top: `${last.top}px`, width: `${last.width}px` },
      ],
      { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );
    this.expandAnimation = animation;

    // cancel() events fire async, so a superseded animation's cleanup must not
    // wipe the inline styles a newer toggle has just applied.
    const cleanup = () => {
      if (this.expandAnimation !== animation) return;
      if (previousInlineStyle) {
        form.setAttribute('style', previousInlineStyle);
      } else {
        form.removeAttribute('style');
      }
      if (wrapper) {
        wrapper.style.width = '';
        wrapper.style.height = '';
      }
    };
    animation.onfinish = cleanup;
    animation.oncancel = cleanup;
  }

  close(clearSearchTerm = false) {
    this.toggleExpandedSearch(false);
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
    document.documentElement.classList.remove('overflow-hidden');
    this.classList.remove('has-term');
  }
}

customElements.define('predictive-search', PredictiveSearch);
